import { useEffect, useState, useRef } from "react";
import { DashboardLayout } from "src/components/dashboard";
import { FeedbacksTable } from "src/components/feedbacks/FeedbacksTable";
import { Feedback } from "src/types/feedback";
import styled from "styled-components";
import { Button } from "src/components/ui";
import { useDeleteFeedbackMutation } from "src/store/services/artisanApi";
import { Loader } from "src/components/common";
import { PaginationProps } from "src/components/ui/Pagination";
import {
  useGetUserFeedbacksQuery,
  useGetUserPinnedFeedbacksQuery,
  usePinUserFeedbackMutation,
  useLazyExportUserFeedbackQuery,
} from "src/store/services/userApi";
import {
  useGetArtisanFeedbacksQuery,
  useGetArtisanPinnedFeedbacksQuery,
  usePinArtisanFeedbackMutation,
  useLazyExportArtisanFeedbackQuery,
} from "src/store/services/artisanApi";

interface ApiResponse<T> {
  success: boolean;
  message: string;
  payload: {
    data: T;
  };
}

interface FeedbacksData {
  feedbacks: Feedback[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

type FeedbackResponse = ApiResponse<FeedbacksData>;

const FeedbacksContainer = styled.div`
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;

    h1 {
      font-size: 32px;
      font-weight: 600;
      color: ${(props) => props.theme.colors.text_01};
    }

    .right-section {
      display: flex;
      gap: 1rem;
      align-items: center;

      .export-button {
        background: ${(props) => props.theme.colors.purple};
        color: white;
        padding: 10px 24px;
        border-radius: 8px;
        border: none;
        cursor: pointer;
        font-weight: 500;

        &:hover {
          opacity: 0.9;
        }
      }

      .search-input {
        padding: 10px 16px;
        border: 1px solid ${(props) => props.theme.colors.gray_03};
        border-radius: 8px;
        width: 300px;
        background: white;

        &::placeholder {
          color: ${(props) => props.theme.colors.text_02};
        }
      }
    }
  }

  .filters {
    display: flex;
    gap: 1rem;
    margin-bottom: 2rem;
    align-items: center;

    .entries {
      font-size: 14px;
      color: ${(props) => props.theme.colors.text_02};
    }

    .tabs {
      display: flex;
      gap: 2rem;
      margin-left: 2rem;

      button {
        background: none;
        border: none;
        padding: 8px 0;
        color: ${(props) => props.theme.colors.text_02};
        cursor: pointer;
        position: relative;
        font-size: 16px;

        &.active {
          color: ${(props) => props.theme.colors.purple};
          font-weight: 500;

          &:after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 2px;
            background: ${(props) => props.theme.colors.purple};
          }
        }
      }
    }

    .right-filters {
      margin-left: auto;
      display: flex;
      gap: 1rem;

      button {
        padding: 8px 16px;
        border-radius: 8px;
        border: 1px solid ${(props) => props.theme.colors.gray_03};
        background: white;
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;

        &:hover {
          background: ${(props) => props.theme.colors.gray_02};
        }
      }
    }
  }
`;

const Feedbacks = () => {
  const [activeTab, setActiveTab] = useState("vendors");
  const [searchTerm, setSearchTerm] = useState("");
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [pgn, setPgn] = useState(1);
  const [userType, setUserType] = useState('artisan');
  const [emojiRating, setEmojiRating] = useState("");
  const [paginationData, setPaginationData] = useState<PaginationProps>({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 1,
  });
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  // RTK Query hooks with conditional fetching
  const queryParams = {
    page: pgn,
    emojiRating: emojiRating,
    limit: 10,
    search: searchTerm,
  };

  // User feedbacks queries
  const {
    data: userFeedbacksData,
    isLoading: isLoadingUserFeedbacks,
    error: userFeedbacksError,
  } = useGetUserFeedbacksQuery(queryParams, {
    skip: activeTab !== "customers",
  });

  const {
    data: userPinnedFeedbacksData,
    isLoading: isLoadingUserPinnedFeedbacks,
    error: userPinnedFeedbacksError,
  } = useGetUserPinnedFeedbacksQuery(queryParams, {
    skip: activeTab !== "pinned_customers",
  });

  // Artisan feedbacks queries
  const {
    data: artisanFeedbacksData,
    isLoading: isLoadingArtisanFeedbacks,
    error: artisanFeedbacksError,
  } = useGetArtisanFeedbacksQuery(queryParams, {
    skip: activeTab !== "vendors",
  });

  const {
    data: artisanPinnedFeedbacksData,
    isLoading: isLoadingArtisanPinnedFeedbacks,
    error: artisanPinnedFeedbacksError,
  } = useGetArtisanPinnedFeedbacksQuery(queryParams, {
    skip: activeTab !== "pinned_vendors",
  });

  // Mutations
  const [pinUserFeedback, { isLoading: isPinningUserFeedback }] =
    usePinUserFeedbackMutation();
  const [pinArtisanFeedback, { isLoading: isPinningArtisanFeedback }] =
    usePinArtisanFeedbackMutation();

  // Export queries
  const [exportUserFeedback, { isLoading: isExportingUserFeedback }] =
    useLazyExportUserFeedbackQuery();
  const [exportArtisanFeedback, { isLoading: isExportingArtisanFeedback }] =
    useLazyExportArtisanFeedbackQuery();

  // Combined loading states
  const isLoading =
    isLoadingUserFeedbacks ||
    isLoadingUserPinnedFeedbacks ||
    isLoadingArtisanFeedbacks ||
    isLoadingArtisanPinnedFeedbacks;
  const isPinning = isPinningUserFeedback || isPinningArtisanFeedback;
  const exportLoading = isExportingUserFeedback || isExportingArtisanFeedback;
  const [deleteTrack, setDeleteTrack] = useState(true)

  // Get current data based on active tab
  const getCurrentData = () => {
    switch (activeTab) {
      case "vendors":
        return artisanFeedbacksData;
      case "customers":
        return userFeedbacksData;
      case "pinned_vendors":
        return artisanPinnedFeedbacksData;
      case "pinned_customers":
        return userPinnedFeedbacksData;
      default:
        return null;
    }
  };

  // Update local state when RTK Query data changes
  useEffect(() => {
    const currentData = getCurrentData();
    if (currentData?.payload?.data) {
      const { feedbacks, pagination } = currentData.payload.data;

      // Use the limit we sent (10) as pageSize if server doesn't provide it
      const pageSize = pagination.pageSize || 10;

      // Calculate total pages as fallback if server doesn't provide it correctly
      const calculatedTotalPages = Math.ceil(pagination.total / pageSize);
      const totalPages = pagination.totalPages || calculatedTotalPages;

      setFeedbacks(feedbacks);
      setPaginationData({
        total: pagination.total,
        page: pgn,
        pageSize: pageSize,
        totalPages: totalPages,
      });
    }
  }, [
    activeTab,
    userFeedbacksData,
    userPinnedFeedbacksData,
    artisanFeedbacksData,
    artisanPinnedFeedbacksData,
    pgn,
    deleteTrack,
  ]);

  useEffect(() => {
    document.title = "Feedback Page";
  }, []);

  const [deleteFeedback] = useDeleteFeedbackMutation();

  const handleDelete = async (feedback: Feedback) => {
    await deleteFeedback({ id: feedback.id, type: userType }).unwrap();
    setDeleteTrack(!deleteTrack)
  };

  const handlePin = async (feedback: Feedback) => {
    try {
      const pinned = !activeTab.includes("pinned_");

      if (activeTab === "vendors" || activeTab === "pinned_vendors") {
        await pinArtisanFeedback({
          id: feedback.id,
          pinned: pinned,
        });
      } else {
        await pinUserFeedback({
          id: feedback.id,
          pinned: pinned,
        });
      }

      // RTK Query will automatically refetch due to cache invalidation
      // No need to manually refresh data
    } catch (error) {
      console.error("Error updating feedback pin status:", error);
    }
  };

  // RTK Query automatically handles data fetching based on activeTab and queryParams
  // No need for manual useEffect to trigger fetching

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setIsFilterDropdownOpen(false);
      }
    };
    if (isFilterDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterDropdownOpen]);

  const handleFilterSelect = (option: string) => {
    setEmojiRating(option);
    setIsFilterDropdownOpen(false);
    setPgn(1);
  };

  const filterOptions = [
    ...new Set(feedbacks.map((feedback) => feedback.emojiRating)),
  ].filter((option) => option !== "");

  const handleExport = async () => {
    try {
      let result;

      if (activeTab === "vendors") {
        result = await exportArtisanFeedback();
      } else {
        result = await exportUserFeedback();
      }

      if (result.data?.payload?.data?.fileUrl) {
        window.open(result.data.payload.data.fileUrl, "_blank");
      }
    } catch (error) {
      console.error("Error exporting feedbacks:", error);
    }
  };

  return (
    <DashboardLayout>
      <FeedbacksContainer>
        <div className="header">
          <h1>Users Feedback</h1>
          <div className="right-section">
            {(activeTab === "vendors" || activeTab === "customers") && (
              <button
                onClick={handleExport}
                className="export-button"
                disabled={exportLoading}
              >
                {exportLoading ? "Exporting..." : "Export"}
              </button>
            )}
            <input
              type="text"
              className="search-input"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="entries font-bold">
              {paginationData.total} entries
            </span>
            <div
              className="flex gap-2"
              ref={filterRef}
              style={{ position: "relative" }}
            >
              <button
                className="flex items-center gap-2"
                onClick={() => setIsFilterDropdownOpen((prev) => !prev)}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 6h18M6 12h12M9 18h6" />
                </svg>
                Filter
              </button>
              {isFilterDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "110%",
                    right: 0,
                    background: "#fff",
                    border: "1px solid #eee",
                    borderRadius: 8,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    zIndex: 10,
                    minWidth: 160,
                  }}
                >
                  {filterOptions.length === 0 && (
                    <div style={{ padding: "10px 16px", color: "#888" }}>
                      No filters available
                    </div>
                  )}
                  {filterOptions.map((option) => (
                    <div
                      key={option}
                      style={{
                        padding: "10px 16px",
                        cursor: "pointer",
                        background:
                          emojiRating === option ? "#f3e8ff" : "transparent",
                        color: emojiRating === option ? "#7607BD" : "#222",
                      }}
                      onClick={() => handleFilterSelect(option)}
                    >
                      {option}
                    </div>
                  ))}
                  {emojiRating && (
                    <div
                      style={{
                        padding: "10px 16px",
                        cursor: "pointer",
                        color: "#d93025",
                        borderTop: "1px solid #eee",
                      }}
                      onClick={() => handleFilterSelect("")}
                    >
                      Clear filter
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="filters border-b-2">
            <div className="tabs">
              <button
                className={activeTab === "vendors" ? "active" : ""}
                onClick={() => {
                  setActiveTab("vendors");
                  setUserType('artisan');
                  setPgn(1);
                }}
              >
                Vendors
              </button>
              <button
                className={activeTab === "customers" ? "active" : ""}
                onClick={() => {
                  setActiveTab("customers");
                  setPgn(1);
                  setUserType('user');
                }}
              >
                Customers
              </button>
              <button
                className={activeTab === "pinned_vendors" ? "active" : ""}
                onClick={() => {
                  setActiveTab("pinned_vendors");
                  setUserType('artisan');
                  setPgn(1);
                }}
              >
                Pinned (Vendors)
              </button>
              <button
                className={activeTab === "pinned_customers" ? "active" : ""}
                onClick={() => {
                  setActiveTab("pinned_customers");
                  setUserType('user');
                  setPgn(1);
                }}
              >
                Pinned (Customers)
              </button>
            </div>
          </div>
          {isLoading ? (
            <div className="flex justify-center items-center h-full py-40">
              <Loader size="large" />
            </div>
          ) : (
            <FeedbacksTable
              feedbacks={feedbacks}
              pgn={pgn}
              setpgn={setPgn}
              paginationData={paginationData}
              onDelete={handleDelete}
              onPin={handlePin}
              isLoading={isLoading}
              activeTab={activeTab}
            />
          )}
        </div>
      </FeedbacksContainer>
    </DashboardLayout>
  );
};

export default Feedbacks;
